/**
 * bc-variant-doc
 *
 * Zero-layout controller that keeps variant-driven content in the product docs
 * panel in sync. Three consumers, all opt-in by the presence of their target:
 *
 *   1. [data-bc-slot="<name>"] anchors — swap href + text to the selected
 *      variant's referenced product (the per-variant `slots` map below), or hide
 *      the line when the variant has no product for that slot (a "No Webbing"
 *      variant). Drives the kit "In The Box" webbing line
 *      (snippets/bc-doc-product-links.liquid).
 *
 *   2. [data-bc-vrow="<handle>"] spec <dd>s — swap innerHTML to the selected
 *      variant's PRE-FORMATTED value (the `rows` map). An EMPTY string means
 *      "this variant has no value for this row" — the dd and its preceding dt
 *      are hidden instead (server renders a hidden skeleton for that case, so
 *      the row can reappear). Drives the Webbing Extender's webbing-sourced
 *      spec rows (sections/bc-product-docs.liquid emits the map).
 *
 *   3. [data-bc-fill="<key>"] — set the element's text to a variant-driven value
 *      (e.g. the kit's webbing length). 4. [data-bc-when="<token>"] — show the
 *      element only when the selected variant carries that token (e.g. a
 *      tensioning-system component group). 5. [data-bc-kit-accessories] zone —
 *      mirror the customer's checked "frequently bought together" accessories
 *      (snippets/bc-accessories) into the docs as a live add-on list.
 *
 * Data: a child <script type="application/json" data-bc-variant-doc> holding
 *   { "slots": { name:   { variantId: {url,title} } },
 *     "rows":  { handle: { variantId: "<html>" } },
 *     "fills": { variantId: { key: "text" } },
 *     "shows": { variantId: ["token", ...] } }
 *
 * Variant id comes from the `variant:update` event (event.detail.resource.id) —
 * the same contract bc-variant-weight / bc-variant-optimistic read. The server
 * pre-fills the default variant, so the initial DOM is already correct (no-JS
 * safe); this only reacts to changes, scoped to this product via
 * event.detail.data.productId.
 */
(() => {
  if (customElements.get('bc-variant-doc')) return;

  class BcVariantDoc extends HTMLElement {
    connectedCallback() {
      try {
        const json = this.querySelector('script[data-bc-variant-doc]')?.textContent || '{}';
        const parsed = JSON.parse(json);
        this.slots = parsed.slots || {};
        this.rows = parsed.rows || {};
        this.fills = parsed.fills || {};
        this.shows = parsed.shows || {};
      } catch {
        this.slots = {};
        this.rows = {};
        this.fills = {};
        this.shows = {};
      }
      this.productId = this.getAttribute('data-product-id') || '';

      // Targets live in the docs panel; the accessories widget is in another
      // section, so scope slot/row/zone lookups to the panel but read the
      // widget from the document.
      const scopeId = this.getAttribute('data-scope');
      this.root = (scopeId && document.getElementById(scopeId)) || document;
      // Cache the accessories widget (server-rendered, present at parse time) so
      // the mirror listeners attach to IT, not the whole document.
      this.widget = document.querySelector('bc-accessories');

      this._onVariantUpdate = this._onVariantUpdate.bind(this);
      this._onWidgetActivity = this._onWidgetActivity.bind(this);
      this._renderAccessories = this._renderAccessories.bind(this);

      document.addEventListener('variant:update', this._onVariantUpdate);
      // Accessory state changes, scoped to the widget. change = checkbox/select,
      // input = typed qty, click = +/- steppers (which set value programmatically,
      // firing no input event). cart:update (document) covers the ride-along reset.
      if (this.widget) {
        this.widget.addEventListener('change', this._onWidgetActivity);
        this.widget.addEventListener('input', this._onWidgetActivity);
        this.widget.addEventListener('click', this._onWidgetActivity);
      }
      document.addEventListener('cart:update', this._renderAccessories);

      // Sync slots/rows to the variant the server rendered (default, or the
      // ?variant= deep-link target), so the slot link's text + href agree from
      // first paint instead of only after the first variant change.
      const initial = this.getAttribute('data-initial-variant');
      if (initial) this._applyVariant(String(initial));

      this._renderAccessories();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:update', this._onVariantUpdate);
      if (this.widget) {
        this.widget.removeEventListener('change', this._onWidgetActivity);
        this.widget.removeEventListener('input', this._onWidgetActivity);
        this.widget.removeEventListener('click', this._onWidgetActivity);
      }
      document.removeEventListener('cart:update', this._renderAccessories);
      if (this._raf) cancelAnimationFrame(this._raf);
    }

    _onVariantUpdate(event) {
      const detail = event && event.detail;
      // Combined-listing / multi-form safety: only react to THIS product.
      const pid = detail && detail.data && detail.data.productId;
      if (pid && this.productId && String(pid) !== String(this.productId)) return;
      const id = detail && detail.resource && detail.resource.id;
      if (id) this._applyVariant(String(id));
    }

    _applyVariant(variantId) {
      // 1. Slots: swap each anchor's href + text to the variant's product, or
      //    HIDE the whole line when this variant has no product for the slot
      //    (e.g. a "No Webbing" kit variant — that line should simply not exist).
      //    The line container is the nearest [data-bc-slot-line], else the
      //    enclosing <li>, else the anchor itself.
      for (const name in this.slots) {
        const data = this.slots[name][variantId];
        this.root.querySelectorAll(`[data-bc-slot="${cssEscape(name)}"]`).forEach((a) => {
          const line = a.closest('[data-bc-slot-line]') || a.closest('li') || a;
          if (!data) {
            line.hidden = true;
            return;
          }
          line.hidden = false;
          if (data.url) a.setAttribute('href', data.url);
          if (data.title) a.textContent = data.title;
        });
      }
      // 2. Rows: swap each spec <dd>'s pre-formatted value. CONTRACT: `rows`
      //    values MUST be server-rendered, Liquid-escaped formatter output
      //    (bc-doc-section-row) — never a raw metafield string — since this
      //    assigns innerHTML. An empty string hides the row (dt + dd); the
      //    server renders a hidden skeleton when the initial variant has no
      //    value, so reveal works in both directions.
      for (const handle in this.rows) {
        const html = this.rows[handle][variantId];
        if (html == null) continue;
        this.root.querySelectorAll(`[data-bc-vrow="${cssEscape(handle)}"]`).forEach((dd) => {
          const off = html === '';
          const dt = dd.previousElementSibling;
          dd.hidden = off;
          if (dt && dt.tagName === 'DT') dt.hidden = off;
          dd.innerHTML = html;
        });
      }
      // 3. Fills: set text on variant-driven spans (e.g. the kit's webbing length).
      const fills = this.fills[variantId];
      if (fills) {
        for (const key in fills) {
          this.root.querySelectorAll(`[data-bc-fill="${cssEscape(key)}"]`).forEach((el) => {
            el.textContent = fills[key];
          });
        }
      }
      // 4. Shows: toggle conditional groups (e.g. tensioning-system blocks). A
      //    [data-bc-when] element is shown iff its token is in this variant's
      //    show list; an empty list hides them all (e.g. a "base only" variant).
      const show = this.shows[variantId];
      if (show) {
        const tokens = new Set(show);
        this.root.querySelectorAll('[data-bc-when]').forEach((el) => {
          el.hidden = !tokens.has(el.getAttribute('data-bc-when'));
        });
      }
    }

    _onWidgetActivity() {
      // Listeners are scoped to the widget, so any activity here is relevant.
      this._scheduleAccessories();
    }

    _scheduleAccessories() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this._renderAccessories();
      });
    }

    _renderAccessories() {
      const zone = this.root.querySelector('[data-bc-kit-accessories]');
      if (!zone) return;
      const list = zone.querySelector('[data-bc-kit-accessories-list]') || zone;
      const items = [];

      // Products already linked in this section's static list -> don't mirror a
      // duplicate (an accessory that's also named as a kit component).
      const section = zone.closest('.bc-product-docs__section') || this.root;
      const already = new Set();
      section.querySelectorAll('a[href]').forEach((a) => {
        if (list.contains(a)) return; // ignore the mirror's own links
        const p = urlPath(a.getAttribute('href'));
        if (p) already.add(p);
      });

      if (this.widget) {
        this.widget.querySelectorAll('[data-bc-acc-row]').forEach((row) => {
          const include = row.querySelector('[data-key="include"]');
          if (!include || !include.checked || include.disabled) return;
          const nameEl = row.querySelector('.bc-accessories__name');
          if (!nameEl) return;
          const url = nameEl.getAttribute('href') || '#';
          const path = urlPath(url);
          if (path && already.has(path)) return; // already a static component
          const qtyEl = row.querySelector('[data-key="qty"]');
          const variantEl = row.querySelector('[data-key="variant"]');
          const qty = Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1);

          // Variant label for multi-variant accessories (skip Shopify's implicit
          // "Default Title"). The <select> option reads "<title> — <price>".
          let vlabel = '';
          if (variantEl && variantEl.tagName === 'SELECT') {
            const opt = variantEl.options[variantEl.selectedIndex];
            const ot = opt && opt.textContent ? opt.textContent.split('—')[0].trim() : '';
            if (ot && ot.toLowerCase() !== 'default title') vlabel = ot;
          }

          items.push({
            title: (nameEl.textContent || '').trim(),
            url,
            qty,
            vlabel,
          });
        });
      }

      if (!items.length) {
        zone.hidden = true;
        list.textContent = '';
        return;
      }

      const frag = document.createDocumentFragment();
      for (const it of items) {
        const li = document.createElement('li');

        // Quantity prefix as plain text, mirroring the static list ("3× <link>").
        if (it.qty > 1) li.appendChild(document.createTextNode(`${it.qty}× `));

        const a = document.createElement('a');
        a.href = it.url;
        a.className = 'bc-doc-prodlink';
        a.textContent = it.vlabel ? `${it.title} (${it.vlabel})` : it.title;
        li.appendChild(a);

        frag.appendChild(li);
      }
      list.textContent = '';
      list.appendChild(frag);
      zone.hidden = false;
    }
  }

  // Attribute-selector hardening. Slot names / handles are simple today, but
  // escape defensively so a future name with quotes/brackets can't break out.
  function cssEscape(s) {
    return window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/["\\\]]/g, '\\$&');
  }

  // Normalize a link to its product path for dedup (drop origin/query/hash and a
  // trailing slash). Returns null for in-page anchors / unparseable hrefs.
  function urlPath(href) {
    if (!href || href.charAt(0) === '#') return null;
    try {
      return new URL(href, location.origin).pathname.replace(/\/+$/, '').toLowerCase();
    } catch {
      return null;
    }
  }

  customElements.define('bc-variant-doc', BcVariantDoc);
})();
